import { useCallback } from "react";

/**
 * useTicketClone - Hook for cloning tickets
 * 
 * Generic hook that works with any ticket type (RFI, T&M, etc.).
 * Fetches the full ticket data, excludes fields that shouldn't be cloned,
 * and creates a new ticket via the API.
 * 
 * @example
 * const { cloneTicket } = useTicketClone({
 *   fetchById: getRFIById,
 *   createTicket: createRFI,
 *   refreshItems: refreshItems,
 * });
 * 
 * // Later, to clone a ticket:
 * await cloneTicket(ticketId);
 * 
 * @param {Object} config
 * @param {Function} config.fetchById - Function to fetch ticket by ID: (id) => Promise<ticket>
 * @param {Function} config.createTicket - Function to create new ticket: (data) => Promise<newTicket>
 * @param {Function} [config.reportNewItem] - If provided (e.g. from ticket context), called with new ticket id after clone for silent refresh + highlight. Prefer over refreshItems.
 * @param {Function} [config.refreshItems] - Fallback: refresh the ticket list after clone (used if reportNewItem not provided)
 * @param {Array<string>} [config.excludeFields] - Additional fields to exclude (beyond defaults)
 * @param {Function} [config.transformPayload] - Optional function to transform the payload before creating the ticket.
 *   Receives the payload and original ticket: (payload, originalTicket) => transformedPayload
 * 
 * @returns {Object} Object with cloneTicket function
 */
export function useTicketClone({
  fetchById,
  createTicket,
  reportNewItem,
  refreshItems,
  excludeFields = [],
  transformPayload,
}) {
  /**
   * Clone a ticket by ID
   * 
   * @param {number|string} ticketId - ID of the ticket to clone
   * @returns {Promise<Object>} The newly created ticket
   */
  const cloneTicket = useCallback(
    async (ticketId) => {
      if (!ticketId) {
        console.error("useTicketClone: ticketId is required");
        return;
      }

      try {
        // Fetch the full ticket data
        const originalTicket = await fetchById(ticketId);
        if (!originalTicket) {
          console.error(`useTicketClone: Ticket with id ${ticketId} not found`);
          return;
        }

        // Fields to always exclude (database IDs, timestamps, audit fields, workflow fields)
        const defaultExcludeFields = [
          "id",
          "rfi_id", // RFI auto-generated ID
          "tm_id", // T&M auto-generated ID (for future use)
          "created_at",
          "updated_at",
          "created_by",
          "submitted",
          "submitted_at",
          "locked",
          "response",
          "response_date",
          "response_by",
          "update_history",
          // Computed/display fields (not actual model fields)
          "job_number",
          "job_description",
          "assigned_to_name",
          "created_by_name",
          "response_by_name",
        ];

        // Combine default and custom exclude fields
        const allExcludeFields = [...defaultExcludeFields, ...excludeFields];

        // Create clone payload by excluding specified fields
        const clonePayload = {};
        Object.entries(originalTicket).forEach(([key, value]) => {
          // Skip excluded fields
          if (allExcludeFields.includes(key)) {
            return;
          }

          // Handle nested objects (like created_by, assigned_to, job)
          // If it's an object with an id, extract just the id
          if (value && typeof value === "object" && !Array.isArray(value) && "id" in value) {
            clonePayload[key] = value.id;
          } else {
            clonePayload[key] = value;
          }
        });

        // Apply transform function if provided (allows modules to customize payload)
        const finalPayload = transformPayload 
          ? transformPayload(clonePayload, originalTicket)
          : clonePayload;

        // Create the new ticket
        const newTicket = await createTicket(finalPayload);

        // Refresh list and optionally highlight new row (reportNewItem does silent refresh + highlight)
        if (reportNewItem && newTicket?.id != null) {
          await reportNewItem(newTicket.id);
        } else if (refreshItems) {
          await refreshItems({ silent: true });
        }

        return newTicket;
      } catch (error) {
        console.error("useTicketClone: Error cloning ticket:", error);
        // Silently fail (no error messages for now)
        throw error;
      }
    },
    [fetchById, createTicket, reportNewItem, refreshItems, excludeFields, transformPayload]
  );

  return { cloneTicket };
}
